/**
 * Quiz results → lead capture.
 *
 * Called from the /quiz results step when the visitor asks us to send them
 * their protocol. Stores the lead record itself unconditionally (data
 * collection, not ad tracking) — marketing consent is stored as a separate
 * boolean and only gates downstream marketing use / ad events.
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const InputSchema = z.object({
  anonymousId: z.string().max(64).optional(),
  name: z.string().trim().max(100).optional(),
  email: z.string().trim().email().max(255),
  phone: z
    .string()
    .trim()
    .max(20)
    .regex(/^[+0-9 \-()]*$/, "invalid phone")
    .optional(),
  consentMarketing: z.boolean(),
  quizAnswers: z.record(z.string(), z.unknown()).default({}),
  recommendedSku: z.string().max(80).optional(),
  supportingSku: z.string().max(80).optional(),
  selectedPackQty: z.number().int().min(1).max(3).optional(),
  valueInr: z.number().min(0).max(1_000_000).optional(),
  eventId: z.string().max(80).optional(),
});

export type SaveQuizLeadInput = z.input<typeof InputSchema>;

export const saveQuizLead = createServerFn({ method: "POST" })
  .inputValidator((data: SaveQuizLeadInput) => InputSchema.parse(data))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const phone = data.phone?.replace(/[^\d+]/g, "") || null;

    const { error } = await supabaseAdmin.from("quiz_leads").insert({
      anonymous_id: data.anonymousId ?? null,
      name: data.name || null,
      email: data.email.toLowerCase(),
      phone,
      consent_marketing: data.consentMarketing,
      quiz_answers: data.quizAnswers as never,
      recommended_sku: data.recommendedSku ?? null,
      supporting_sku: data.supportingSku ?? null,
      selected_pack_qty: data.selectedPackQty ?? null,
      value_inr: data.valueInr ?? null,
      event_id: data.eventId ?? null,
    } as never);

    if (error) {
      console.error("[quiz-lead] insert failed", error.message);
      return { ok: false as const, error: "save_failed" as const };
    }

    // Mirror the quiz lead into the shared lead funnel so it also shows in
    // Admin → Leads. Nothing here marks the contact as verified: no
    // phone_verified_at / email_verified_at / consent_whatsapp, so all
    // downstream send gates keep treating it as unverified.
    const email = data.email.toLowerCase();
    const anonymousId = data.anonymousId || `quiz:${email}`.slice(0, 64);
    const now = new Date().toISOString();
    const orFilter = phone
      ? `anonymous_id.eq.${anonymousId},email.eq.${email},phone.eq.${phone}`
      : `anonymous_id.eq.${anonymousId},email.eq.${email}`;
    try {
      // Link to an existing customer record when this email or phone has
      // purchased or signed in before, so the quiz lead lands on the same
      // person instead of creating a duplicate contact.
      let customerId: string | null = null;
      try {
        const { data: cust } = await supabaseAdmin
          .from("customers")
          .select("id")
          .or(phone ? `email.eq.${email},phone.eq.${phone}` : `email.eq.${email}`)
          .limit(1)
          .maybeSingle();
        customerId = cust?.id ?? null;
      } catch (err) {
        console.warn("[quiz-lead] customer lookup failed", (err as Error).message);
      }

      // Reuse an existing lead profile matched on anonymous id, email or phone.
      const { data: existing } = await supabaseAdmin
        .from("lead_profiles")
        .select("id, email, phone, customer_id")
        .or(orFilter)
        .order("created_at", { ascending: true })
        .limit(1)
        .maybeSingle();

      let profileId = existing?.id ?? null;

      if (existing) {
        await supabaseAdmin
          .from("lead_profiles")
          .update({
            email: existing.email ?? email,
            phone: existing.phone ?? phone,
            last_seen: now,
            last_route: "/quiz",
            last_product_handle: data.recommendedSku ?? null,
            ...(customerId && !existing.customer_id
              ? { customer_id: customerId, matched_at: now }
              : {}),
          } as never)
          .eq("id", existing.id);
      } else {
        const { data: profile, error: profileError } = await supabaseAdmin
          .from("lead_profiles")
          .upsert(
            {
              anonymous_id: anonymousId,
              email,
              phone,
              last_seen: now,
              last_route: "/quiz",
              last_product_handle: data.recommendedSku ?? null,
              ...(customerId ? { customer_id: customerId, matched_at: now } : {}),
            } as never,
            { onConflict: "anonymous_id" },
          )
          .select("id")
          .maybeSingle();
        profileId = profile?.id ?? null;
        if (!profileId) console.warn("[quiz-lead] lead upsert failed", profileError?.message);
      }

      await supabaseAdmin.from("lead_events").insert({
        anonymous_id: anonymousId,
        lead_profile_id: profileId,
        event_type: "quiz_lead",
        properties: {
          recommended_sku: data.recommendedSku ?? null,
          pack_qty: data.selectedPackQty ?? null,
          value_inr: data.valueInr ?? null,
          consent_marketing: data.consentMarketing,
        } as never,
      } as never);
    } catch (err) {
      console.warn("[quiz-lead] lead mirror failed", (err as Error).message);
    }

    return { ok: true as const };
  });
